import { useLayoutEffect, useRef, useState, type FunctionComponent } from "react";
import {
  DeleteOutlined,
  EditOutlined,
  EllipsisOutlined,
  MessageOutlined,
  PushpinOutlined,
  SmileOutlined,
} from "@ant-design/icons";
import { Avatar, Button, Dropdown, Popover, Space, Tag, Typography } from "antd";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

import { CHALLENGE_POST_REACTIONS, type ChallengeFeedPost } from "@fitness-app/data-models/entities/ChallengeFeed";

import ChallengeFeedMarkdown from "../ChallengeFeedMarkdown";
import { ChallengeMediaGallery } from "./ChallengeMediaGallery";

dayjs.extend(relativeTime);

/** Above this the body is clipped and gets a "Pokaż więcej" toggle — long posts must not bury the feed. */
const CLAMP_HEIGHT = 260;

type Props = {
  post: ChallengeFeedPost;
  onOpenThread: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onTogglePin: () => void;
  onToggleReaction: (emoji: string) => void;
  onShowReactors: () => void;
};

/**
 * One post on the board, laid out like a community feed card (Circle): identity row, body, media,
 * then a thin engagement bar. Moderation lives in the "…" menu so it never competes with the content.
 */
const ChallengeFeedPostCard: FunctionComponent<Props> = ({
  post,
  onOpenThread,
  onEdit,
  onDelete,
  onTogglePin,
  onToggleReaction,
  onShowReactors,
}) => {
  const bodyRef = useRef<HTMLDivElement>(null);
  const [isClamped, setClamped] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // Only offer "Pokaż więcej" when the body is actually taller than the clamp.
  useLayoutEffect(() => {
    const element = bodyRef.current;
    if (element) {
      setClamped(element.scrollHeight > CLAMP_HEIGHT + 24);
    }
  }, [post.title, post.content]);

  const reactionEntries = Object.entries(post.reactions);
  const reactionTotal = reactionEntries.reduce((total, [, count]) => total + count, 0);

  return (
    <article className="rounded-2xl border border-[#f0f0f0] bg-white p-5 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
      <div className="flex items-start gap-3">
        <Avatar size={40} src={post.authorAvatar ?? undefined}>
          {post.authorName.charAt(0).toUpperCase()}
        </Avatar>
        <div className="min-w-0 flex-1">
          <Space size={6} align="center" wrap>
            <Typography.Text strong>{post.authorName}</Typography.Text>
            {post.authorIsTrainer ? (
              <Tag color="gold" bordered={false}>
                Trener
              </Tag>
            ) : (
              <Tag bordered={false}>Uczestniczka</Tag>
            )}
            {post.pinned ? (
              <Tag color="blue" bordered={false} icon={<PushpinOutlined />}>
                Przypięty
              </Tag>
            ) : null}
          </Space>
          <div>
            <Typography.Text
              type="secondary"
              style={{ fontSize: 12 }}
              title={dayjs(post.createdAt).format("DD.MM.YYYY HH:mm")}
            >
              {dayjs(post.createdAt).fromNow()}
              {post.updatedAt !== post.createdAt ? " · edytowany" : ""}
            </Typography.Text>
          </div>
        </div>
        <Dropdown
          trigger={["click"]}
          menu={{
            items: [
              {
                key: "pin",
                icon: <PushpinOutlined />,
                label: post.pinned ? "Odepnij" : "Przypnij",
                onClick: onTogglePin,
              },
              ...(post.isMine ? [{ key: "edit", icon: <EditOutlined />, label: "Edytuj", onClick: onEdit }] : []),
              { type: "divider" as const },
              { key: "delete", icon: <DeleteOutlined />, label: "Usuń", danger: true, onClick: onDelete },
            ],
          }}
        >
          <Button type="text" icon={<EllipsisOutlined />} />
        </Dropdown>
      </div>

      <div className="relative mt-3">
        <div ref={bodyRef} style={!expanded && isClamped ? { maxHeight: CLAMP_HEIGHT, overflow: "hidden" } : undefined}>
          {post.title ? (
            <Typography.Title level={5} style={{ marginTop: 0, marginBottom: 8 }}>
              {post.title}
            </Typography.Title>
          ) : null}
          <ChallengeFeedMarkdown content={post.content} />
        </div>
        {isClamped && !expanded ? (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-white to-transparent" />
        ) : null}
      </div>
      {isClamped ? (
        <Button type="link" className="px-0" onClick={() => setExpanded((current) => !current)}>
          {expanded ? "Pokaż mniej" : "Pokaż więcej"}
        </Button>
      ) : null}

      {/* Outside the clamp on purpose: the clamp exists to stop long PROSE from burying the feed.
          With the attachments inside it, every post carrying media was cut in half by the fade. */}
      <ChallengeMediaGallery media={post.media} />

      <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-[#f5f5f5] pt-3">
        {reactionEntries.map(([emoji, count]) => (
          <button
            key={emoji}
            type="button"
            onClick={() => onToggleReaction(emoji)}
            className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-sm transition-colors ${
              post.myReactions.includes(emoji)
                ? "border-[#ffd591] bg-[#fff7e6] text-[#d46b08]"
                : "border-[#f0f0f0] bg-white hover:border-[#d9d9d9]"
            }`}
          >
            <span>{emoji}</span>
            <span className="text-xs">{count}</span>
          </button>
        ))}

        <Popover
          trigger="click"
          content={
            <Space size={4}>
              {CHALLENGE_POST_REACTIONS.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  onClick={() => onToggleReaction(emoji)}
                  className="rounded-full px-2 py-1 text-xl hover:bg-[#f5f5f5]"
                >
                  {emoji}
                </button>
              ))}
            </Space>
          }
        >
          <Button type="text" size="small" icon={<SmileOutlined />} />
        </Popover>

        <div className="flex-1" />

        {reactionTotal ? (
          <Button type="link" size="small" className="px-0" onClick={onShowReactors}>
            {reactionTotal} {reactionTotal === 1 ? "reakcja" : "reakcji"}
          </Button>
        ) : null}

        <Button type="text" size="small" icon={<MessageOutlined />} onClick={onOpenThread}>
          {post.commentCount ? `Komentarze (${post.commentCount})` : "Skomentuj"}
        </Button>
      </div>
    </article>
  );
};

export default ChallengeFeedPostCard;
